import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { styled } from '@mui/material/styles';
import { FormattedMessage } from 'react-intl';
import CircularProgress from '@mui/material/CircularProgress';
import DownloadIcon from '@/assets/download.svg?react';
import Button from '@/components/Button/Button';
import FileGrid from '@/components/FileExplorer/FileGrid';
import DisableLinkConfirm, {
  DangerButton,
} from '@/components/ShareBasketModal/DisableLinkConfirm';
import { fetchSharedBasket } from '@/lib/api';
import { formatSharedBasketDate } from '@/lib/sharedBasketDate';
import { getSharedBasketErrorCode } from '@/lib/sharedBasketError';
import { sharedBasketQueryKeys } from '@/lib/sharedBasketQueryKeys';
import {
  ComplianceDocument,
  MarketingAssetSchema,
  SharedBasketItemCategory,
  SharedBasketItemInput,
} from '@/lib/types';
import { useAuthContext } from '@/contexts/AuthContext';
import { useShareBasket } from '@/hooks/useShareBasket';
import { useSharedBasketDownload } from '@/hooks/useSharedBasketDownload';
import NotFoundPage from '@/pages/NotFoundPage/NotFoundPage';
import SharedBasketInactive from './SharedBasketInactive';
import SharedBasketNotRecipient from './SharedBasketNotRecipient';

// Selection is kept per category, never as one id list: compliance #42 and
// marketing #42 are different files and a basket may hold both, so a single
// list of ids could not say which one the recipient ticked.
type SelectionState = Record<SharedBasketItemCategory, number[]>;

const EMPTY_SELECTION: SelectionState = {
  compliance_documents: [],
  marketing_assets: [],
};

const SharedBasketPage = () => {
  const { token = '' } = useParams();
  const { user } = useAuthContext();
  const [selection, setSelection] =
    useState<SelectionState>(EMPTY_SELECTION);
  const [confirmingDisable, setConfirmingDisable] = useState(false);

  useEffect(() => {
    setSelection(EMPTY_SELECTION);
    setConfirmingDisable(false);
  }, [token]);

  const {
    data: basket,
    isPending,
    isError,
    error,
  } = useQuery({
    queryKey: [...sharedBasketQueryKeys.byToken(token), user?.id],
    queryFn: () => fetchSharedBasket(token),
    staleTime: 0,
    retry: false,
    enabled: !!token,
  });

  const {
    disable,
    isDisabling,
    disableError,
    clearDisableError,
  } = useShareBasket();
  const { download, isDownloading } = useSharedBasketDownload(token);

  const complianceFiles = useMemo<ComplianceDocument[]>(
    () => basket?.files.compliance_documents ?? [],
    [basket?.files.compliance_documents],
  );
  const marketingFiles = useMemo<MarketingAssetSchema[]>(
    () => basket?.files.marketing_assets ?? [],
    [basket?.files.marketing_assets],
  );
  const totalCount = complianceFiles.length + marketingFiles.length;
  const selectedItems = useMemo<SharedBasketItemInput[]>(
    () =>
      (
        Object.keys(EMPTY_SELECTION) as SharedBasketItemCategory[]
      ).flatMap((category) =>
        selection[category].map((file_id) => ({
          category,
          file_id,
        })),
      ),
    [selection],
  );

  const selectCategory =
    (category: SharedBasketItemCategory) => (ids: number[]) =>
      setSelection((previous) => ({ ...previous, [category]: ids }));

  const handleDownload = () => {
    if (!totalCount) return;
    download(selectedItems.length ? selectedItems : undefined);
  };

  if (isError) {
    const code = getSharedBasketErrorCode(error);

    if (code === 'NOT_INTENDED_RECIPIENT') {
      return <SharedBasketNotRecipient token={token} />;
    }
    if (code === 'LINK_EXPIRED') {
      return <SharedBasketInactive kind="expired" />;
    }
    if (code === 'LINK_DISABLED') {
      return <SharedBasketInactive kind="disabled" />;
    }
    // NOT_FOUND, and anything unrecognised: a bad token is indistinguishable
    // from a bad URL, and neither may hint that a basket exists.
    return <NotFoundPage />;
  }

  // Nothing basket-shaped before the server has answered - no counts, no
  // token, no skeleton rows.
  if (isPending || !basket) {
    return (
      <LoaderWrapper>
        <CircularProgress />
      </LoaderWrapper>
    );
  }

  const downloadCount = selectedItems.length || totalCount;

  return (
    <Page>
      <TitleRow>
        <Title>
          <FormattedMessage
            id="sharedDownloadBasket"
            defaultMessage="Shared Download Basket"
          />
        </Title>
        <TitleActions>
          {basket.can_disable && !confirmingDisable && (
            <DangerButton
              variant="outlined"
              onClick={() => {
                clearDisableError();
                setConfirmingDisable(true);
              }}
              aria-label="Disable link"
            >
              <FormattedMessage
                id="disableLink"
                defaultMessage="Disable link"
              />
            </DangerButton>
          )}
          <Button
            variant="contained"
            onClick={handleDownload}
            disabled={!totalCount || isDownloading}
            loading={isDownloading}
            aria-label={
              selectedItems.length
                ? 'Download selected files'
                : 'Download all files in this basket'
            }
          >
            <DownloadIcon />
            {selectedItems.length ? (
              <FormattedMessage
                id="downloadSelectedCount"
                defaultMessage="Download selected ({count})"
                values={{ count: downloadCount }}
              />
            ) : (
              <FormattedMessage
                id="downloadAllCount"
                defaultMessage="Download all ({count})"
                values={{ count: downloadCount }}
              />
            )}
          </Button>
        </TitleActions>
      </TitleRow>

      <Subtitle>
        <FormattedMessage
          id="availableUntil"
          defaultMessage="Available until {date}"
          values={{ date: formatSharedBasketDate(basket.expires_at) }}
        />
      </Subtitle>

      {basket.can_disable && (
        <InfoBanner>
          {basket.is_creator ? (
            <FormattedMessage
              id="sharedBasketCreatorBanner"
              defaultMessage="This is your link. You're seeing it exactly as a recipient does — Disable link stops it working for everyone, immediately and permanently."
            />
          ) : (
            <FormattedMessage
              id="sharedBasketAdminBanner"
              defaultMessage="This link was created by someone else. You can disable it because your role outranks theirs. Doing so stops it working for every recipient, immediately and permanently — and the creator is not asked first."
            />
          )}
        </InfoBanner>
      )}

      {basket.has_unavailable_files && (
        <WarningBanner role="status">
          <FormattedMessage
            id="sharedBasketUnavailableFiles"
            defaultMessage="Some files in this basket are not available based on your permissions."
          />
        </WarningBanner>
      )}

      {confirmingDisable && (
        <DisableLinkConfirm
          error={disableError}
          isDisabling={isDisabling}
          onConfirm={() => disable(basket.token)}
          onCancel={() => setConfirmingDisable(false)}
        />
      )}

      {totalCount === 0 ? (
        <Empty>
          <FormattedMessage
            id="sharedBasketNoFiles"
            defaultMessage="None of the files in this basket are available to you."
          />
        </Empty>
      ) : (
        <>
          {complianceFiles.length > 0 && (
            <Section>
              <SectionTitle>
                <FormattedMessage
                  id="complianceDocuments"
                  defaultMessage="Compliance Documents"
                />
                <SectionCount>{complianceFiles.length}</SectionCount>
              </SectionTitle>
              <FileGrid
                type="compliance"
                files={complianceFiles}
                selected={selection.compliance_documents}
                onSelect={selectCategory('compliance_documents')}
                // Both of these must go through the link's own endpoint:
                // /api/download knows nothing about this link's expiry or
                // disabled state and would keep serving a dead basket.
                onFileDownload={(file) =>
                  download([
                    {
                      category: 'compliance_documents',
                      file_id: file.id,
                    },
                  ])
                }
                downloading={isDownloading}
              />
            </Section>
          )}

          {marketingFiles.length > 0 && (
            <Section>
              <SectionTitle>
                <FormattedMessage
                  id="marketingAssets"
                  defaultMessage="Marketing Assets"
                />
                <SectionCount>{marketingFiles.length}</SectionCount>
              </SectionTitle>
              <FileGrid
                type="marketing"
                files={marketingFiles}
                selected={selection.marketing_assets}
                onSelect={selectCategory('marketing_assets')}
                onFileDownload={(file) =>
                  download([
                    {
                      category: 'marketing_assets',
                      file_id: file.id,
                    },
                  ])
                }
                downloading={isDownloading}
              />
            </Section>
          )}
        </>
      )}
    </Page>
  );
};

const LoaderWrapper = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100vh',
});

const Page = styled('div')(({ theme }) => ({
  maxWidth: 1280,
  width: '100%',
  margin: '0 auto',
  padding: theme.spacing(4, 3),
}));

const TitleRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  flexWrap: 'wrap',
}));

const Title = styled('h1')(({ theme }) => ({
  margin: 0,
  fontSize: 24,
  lineHeight: '28px',
  color: theme.palette.grey900,
}));

const TitleActions = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
}));

const Subtitle = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(1),
  fontSize: 14,
  fontWeight: 600,
  color: theme.palette.grey600,
}));

const Banner = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(2),
  padding: theme.spacing(1.5),
  borderRadius: theme.radii.sm,
  fontSize: 13,
  lineHeight: '18px',
}));

const InfoBanner = styled(Banner)(({ theme }) => ({
  border: `1px solid ${theme.palette.grey200}`,
  background: theme.palette.grey100,
  color: theme.palette.grey600,
}));

const WarningBanner = styled(Banner)(({ theme }) => ({
  border: `1px solid ${theme.palette.yellow500}`,
  background: theme.palette.yellow50,
  color: theme.palette.grey900,
}));

const Empty = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(3),
  padding: theme.spacing(4),
  textAlign: 'center',
  borderRadius: theme.radii.sm,
  border: `1px dashed ${theme.palette.grey300}`,
  color: theme.palette.grey500,
  fontSize: 14,
}));

const Section = styled('section')(({ theme }) => ({
  marginTop: theme.spacing(4),
}));

const SectionTitle = styled('h2')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  margin: 0,
  marginBottom: theme.spacing(2),
  fontSize: 16,
  lineHeight: '24px',
  color: theme.palette.grey900,
}));

const SectionCount = styled('span')(({ theme }) => ({
  padding: theme.spacing(0, 1),
  borderRadius: theme.radii.xs,
  background: theme.palette.grey100,
  color: theme.palette.grey600,
  fontSize: 13,
  fontWeight: 500,
}));

export default SharedBasketPage;
